/**
 * Manifest registry for split production builds.
 * Game controllers and assets load from isolated per-game bundles (game.js).
 */
import type { GameAssetModule, GameManifest } from '@platform/contracts';
import type {
  AppGameControllerModule,
  AppGameRegistryEntry,
} from './game-registry-types';

import coreManifest from '../../current/core.json';
import crashManifest from '../../current/games/crash.json';
import skyXManifest from '../../current/games/sky-x.json';
import skyxManifest from '../../current/games/skyx.json';
import skyxNewManifest from '../../current/games/skyx_new.json';
import skySnifferManifest from '../../current/games/sky-sniffer.json';
import skySnifferV2Manifest from '../../current/games/sky-sniffer-v2.json';
import superdogManifest from '../../current/games/superdog.json';
import caveCrasherManifest from '../../current/games/cave-crasher.json';
import chickenluckManifest from '../../current/games/chickenluck.json';
import citySnifferManifest from '../../current/games/city-sniffer.json';

const gameManifests: Record<string, GameManifest> = {
  crash: crashManifest as GameManifest,
  'sky-x': skyXManifest as GameManifest,
  skyx: skyxManifest as GameManifest,
  skyx_new: skyxNewManifest as GameManifest,
  'sky-sniffer': skySnifferManifest as GameManifest,
  'sky-sniffer-v2': skySnifferV2Manifest as GameManifest,
  superdog: superdogManifest as GameManifest,
  'cave-crasher': caveCrasherManifest as GameManifest,
  chickenluck: chickenluckManifest as GameManifest,
  'city-sniffer': citySnifferManifest as GameManifest,
};

function resolveDefaultGamesBase(): string {
  const envOverride = (
    import.meta as ImportMeta & { env?: { VITE_GAMES_BASE?: string } }
  ).env?.VITE_GAMES_BASE;
  if (envOverride) {
    return envOverride;
  }
  const base = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');
  if (!base || base === '/') {
    return '/games';
  }
  return `${base.startsWith('/') ? base : `/${base}`}/games`;
}

const DEFAULT_GAMES_BASE = resolveDefaultGamesBase();

function gameBundleUrl(routeSlug: string, gamesBase: string): string {
  const base = gamesBase.replace(/\/$/, '');
  return `${base}/${routeSlug}/game.js`;
}

function loadGameModule(
  routeSlug: string,
  gamesBase: string
): Promise<AppGameControllerModule & GameAssetModule> {
  return import(/* @vite-ignore */ gameBundleUrl(routeSlug, gamesBase));
}

export function getCoreManifest() {
  return coreManifest;
}

export function getGameManifest(slug: string): GameManifest | null {
  return gameManifests[slug] ?? null;
}

export function listGameManifests(): GameManifest[] {
  return Object.values(gameManifests);
}

export function listEnabledGameManifests(): GameManifest[] {
  return listGameManifests().filter(m => m.enabled);
}

export function resolveControllerImport(slug: string): string | null {
  return getGameManifest(slug)?.controllerModule ?? null;
}

export function assertManifestRegistryValid(): void {
  const enabled = listEnabledGameManifests();
  if (enabled.length === 0) {
    throw new Error('[manifest-loader.runtime] No enabled game manifests');
  }
}

function manifestToRegistryEntry(
  manifest: GameManifest,
  gamesBase: string
): AppGameRegistryEntry {
  return {
    routeSlug: manifest.routeSlug,
    audioSlug: manifest.audioSlug,
    loadController: () =>
      loadGameModule(manifest.routeSlug, gamesBase).then(mod => ({
        CrashGameController: mod.CrashGameController,
      })),
    ...(manifest.assetModule
      ? {
          loadAssets: () =>
            loadGameModule(manifest.routeSlug, gamesBase).then(mod => ({
              CRASH_ASSETS: mod.CRASH_ASSETS,
              AVIATOR_ASSETS: mod.AVIATOR_ASSETS,
            })),
        }
      : {}),
  };
}

export function getGamesBase(): string {
  return DEFAULT_GAMES_BASE;
}

/** Injects per-game stylesheet when using split builds (VITE_SPLIT_BUILD). */
export function ensureGameStylesLoaded(
  routeSlug: string,
  gamesBase = DEFAULT_GAMES_BASE
): void {
  const split =
    (import.meta as ImportMeta & { env?: { VITE_SPLIT_BUILD?: string } }).env
      ?.VITE_SPLIT_BUILD === 'true';
  if (!split) return;

  const id = `pg-game-style-${routeSlug}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `${gamesBase.replace(/\/$/, '')}/${routeSlug}/game.css`;
  document.head.appendChild(link);
}

export function buildGameRegistry(
  gamesBase = DEFAULT_GAMES_BASE
): Record<string, AppGameRegistryEntry> {
  assertManifestRegistryValid();

  const registry: Record<string, AppGameRegistryEntry> = {};
  for (const manifest of listEnabledGameManifests()) {
    registry[manifest.routeSlug] = manifestToRegistryEntry(manifest, gamesBase);
  }

  if ((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV) {
    console.debug('[manifest-loader.runtime] gamesBase:', gamesBase);
    for (const m of listEnabledGameManifests()) {
      console.debug(
        `  ${m.routeSlug} → ${gameBundleUrl(m.routeSlug, gamesBase)}`
      );
    }
  }

  return registry;
}
